import React from 'react';
import classNames from 'classnames';

import { GoogleIcon } from 'assets/svgs/GoogleIcon';
import { AppleIcon } from 'assets/svgs/AppleIcon';
import { getFocusOutline } from 'styles/accessibility';

interface SocialLoginButtonsProps {
    onGoogleSignIn?: () => void;
    onAppleSignIn?: () => void;
    onDisconnect?: () => void;
    connectedProvider?: 'google' | 'apple' | null;
    dividerLabel?: string;
    showDivider?: boolean;
    showGoogle?: boolean;
    showApple?: boolean;
    googleLabel?: string;
    appleLabel?: string;
    labelAlignment?: 'center' | 'left';
}

const socialButtonClassName = classNames(
    'w-full flex items-center justify-center gap-4 px-5 py-3',
    'border border-grey-3 rounded-full bg-white',
    'text-grey-1 font-semibold text-sm',
    'transition-colors hover:bg-grey-5',
    getFocusOutline({ borderRadius: 9999, offset: 4 })
);

const disconnectButtonClassName = classNames(
    'ml-auto text-xs text-brand font-semibold hover:underline',
    getFocusOutline({ borderRadius: 4, offset: 2 })
);

const Divider = ({ label }: { label: string }) => (
    <div className="flex items-center my-5">
        <div className="flex-1 h-px bg-grey-3" />
        <span className="mx-3 text-sm text-grey-medium">{label}</span>
        <div className="flex-1 h-px bg-grey-3" />
    </div>
);

export const SocialLoginButtons = ({
    onGoogleSignIn,
    onAppleSignIn,
    onDisconnect,
    connectedProvider,
    dividerLabel = 'or',
    showDivider = true,
    showGoogle = false,
    showApple = false,
    googleLabel = 'Continue with Google',
    appleLabel = 'Continue with Apple',
    labelAlignment = 'center',
}: SocialLoginButtonsProps) => {
    const labelClassName = labelAlignment === 'left' ? 'flex-1 text-left' : 'flex-1 text-center';

    if (connectedProvider) {
        return (
            <div>
                <div className="flex items-center w-full px-5 py-3 border border-grey-3 rounded-full bg-white">
                    {connectedProvider === 'google' ? <GoogleIcon /> : <AppleIcon />}
                    <div className="ml-3 flex flex-col leading-none">
                        <span className="text-xs text-grey-medium mb-0.5">(Connected)</span>
                        <span className="text-sm font-semibold text-grey-1">
                            {connectedProvider === 'google' ? 'Google' : 'Apple'}
                        </span>
                    </div>
                    {onDisconnect && (
                        <button
                            type="button"
                            onClick={onDisconnect}
                            className={disconnectButtonClassName}
                        >
                            Disconnect
                        </button>
                    )}
                </div>
                {showDivider && <Divider label={dividerLabel} />}
            </div>
        );
    }

    if (!showGoogle && !showApple) {
        return null;
    }

    return (
        <div>
            <div className="flex flex-col gap-3">
                {showGoogle && (
                    <button
                        type="button"
                        onClick={onGoogleSignIn}
                        className={socialButtonClassName}
                    >
                        <GoogleIcon />
                        <span className={labelClassName}>{googleLabel}</span>
                    </button>
                )}
                {showApple && (
                    <button type="button" onClick={onAppleSignIn} className={socialButtonClassName}>
                        <AppleIcon />
                        <span className={labelClassName}>{appleLabel}</span>
                    </button>
                )}
            </div>
            {showDivider && <Divider label={dividerLabel} />}
        </div>
    );
};
